import {
  CartFamilies,
  LicenseHolder,
  LicenseHolderCompanySize,
  LicenseUsage,
} from "../models/Licensing";
import { FamilyLineItem, LineItem, LineItems } from "../models/LineItems";
import {
  FoundryApiError,
  FoundryApiErrorCode,
  ProductDoesNotExistError,
  ProductNotAvailableError,
} from "../foundry_client/errors";
import { PRODUCTS, Product } from "../models/Products";
import {
  PhysicalProductState,
  PhysicalProductsState,
} from "../models/PhysicalProducts";
import {
  getDiscountCode,
  getFontLicenseTypes,
  getFontStyles,
  getLicenseHolder,
  getLicenseUsage,
  getPhysicalProducts,
} from "./CartStorage";

import {
  PhysicalProductDiscounts,
  findPhysicalProductDiscounts,
} from "../models/Discounts";

import { FONT_SETTINGS } from "../../fonts";
import FoundryPhysicalProduct from "../foundry_client/models/FoundryPhysicalProduct";
import { TotalDetails } from "../models/CartTotals";

// Main method that retrieves cart details from local storage. This is used by
// The global cart context, which provides the cart state for the site-wide view.
export const getCartDetails = (): CartDetails | null => {
  const licenseUsage = getLicenseUsage();
  const licenseUsageDetails = licenseUsage
    ? {
        isEducationalLicense: !!licenseUsage.license_usage_for_education,
        isOnBehalfOfSomeoneElse: !!licenseUsage.license_usage_on_behalf_of,
      }
    : undefined;

  const cartDetails = new CartDetails(licenseUsageDetails);
  cartDetails.licenseHolder = getCartLicenseHolder();
  cartDetails.families = getCartFamilies();
  cartDetails.physicalProducts = getCartPhysicalProducts();
  cartDetails.discountCode = getDiscountCode();
  return cartDetails;
};

export class CartDetails {
  licenseUsage?: LicenseUsage;
  licenseHolder?: LicenseHolder;
  families: CartFamilies;
  physicalProducts: PhysicalProductsState;
  discountCode?: string | null;

  constructor(licenseUsage?: LicenseUsage) {
    this.licenseUsage = licenseUsage;
    this.families = {};
    this.physicalProducts = [];
  }

  itemCount(): number {
    let count = 0;

    if (this.families) {
      count += Object.values(this.families)
        .map((f) => f.items)
        .flat().length;
    }

    if (this.physicalProducts) {
      count += this.physicalProducts.reduce(
        (accum, p) => accum + p.quantity,
        0
      );
    }

    return count;
  }

  isEmpty(): boolean {
    return !!this.itemCount();
  }

  familiesWithItems(): string[] {
    if (this.families) {
      return Object.entries(this.families)
        .filter(([name, fam]) => fam.items.length > 0)
        .map(([name, fam]) => name);
    }

    return [];
  }

  viewingNewFamily(family: string): boolean {
    return !this.familiesWithItems()
      .map((f) => f.toLowerCase())
      .includes(family.toLowerCase());
  }

  companySizeString(): string {
    return this.licenseHolder?.companySize == "unlimited"
      ? this.licenseHolder.companySize
      : `< ${this.licenseHolder?.companySize}`;
  }

  // The discounts applied to the physical products in the cart, keyed by Contentful ID.
  physicalProductDiscounts(
    physicalProducts: FoundryPhysicalProduct[]
  ): PhysicalProductDiscounts {
    return findPhysicalProductDiscounts(
      this.physicalProducts || [],
      physicalProducts
    );
  }

  totalDetails(
    physicalProducts: FoundryPhysicalProduct[]
  ): [LineItems, TotalDetails] {
    const familyLineItems: { [k: string]: FamilyLineItem } = {};
    const physicalLineItems: LineItem[] = [];

    const details = {
      subtotal: 0,
      styleDiscountsTotal: 0,
      productDiscountsTotal: 0,
      total: 0,
    };

    Object.entries(this.families).forEach(([familyName, familyDetails]) => {
      if (familyDetails.items.length > 0) {
        familyLineItems[familyName] = {
          familyName,
          platforms: familyDetails.platforms,
          items: [],
        };
      }

      familyDetails.items.forEach((entry) => {
        const price = entry.getBasePrice(
          familyDetails.platforms.length,
          this.licenseUsage?.isEducationalLicense || false
        );

        const styleDiscount =
          entry.getDiscount(familyDetails.items)?.discount || 0;

        details.subtotal += price;
        details.styleDiscountsTotal += styleDiscount * price;

        familyLineItems[familyName].items.push({
          id: entry.id,
          name: entry.name,
          price,
          discountPercentage: styleDiscount,
        });
      });
    });

    if (this.physicalProducts) {
      const productDiscounts = this.physicalProductDiscounts(physicalProducts);

      this.physicalProducts.forEach((product) => {
        const productDetails = physicalProducts?.find(
          (p) => p.id === product.contentfulId
        );

        if (!productDetails) {
          // TODO: Throw an error
          return;
        }

        const discountCents = productDiscounts[product.contentfulId] || 0;

        details.subtotal += productDetails.priceCents * product.quantity;
        details.productDiscountsTotal += discountCents;
        physicalLineItems.push({
          id: product.contentfulId,
          name: productDetails.name,
          price: productDetails.priceCents * product.quantity,
          quantity: product.quantity,
          discountPercentage: 0,
          discountCents,
        });
      });
    }

    details.total =
      details.subtotal -
      details.styleDiscountsTotal -
      details.productDiscountsTotal;

    const lineItems = [...Object.values(familyLineItems), ...physicalLineItems];
    return [lineItems, details];
  }

  static getPhysicalProductError(
    physicalProductDetails: FoundryPhysicalProduct[],
    productState: PhysicalProductState
  ): FoundryApiError | null {
    const productDetails = physicalProductDetails?.find(
      (p) => p.id === productState.contentfulId
    );

    if (!productDetails) {
      return new ProductDoesNotExistError(productState.contentfulId);
    }

    return CartDetails.getPhysicalProductErrorHelper(
      productDetails,
      productState
    );
  }

  static getPhysicalProductErrorHelper(
    productDetails: FoundryPhysicalProduct,
    productState: PhysicalProductState
  ): FoundryApiError | null {
    if (
      productDetails.orderedQuantity + productState.quantity >
      productDetails.maxQuantity
    ) {
      return new ProductNotAvailableError(
        productState.contentfulId,
        productDetails.name,
        productDetails.maxQuantity - productDetails.orderedQuantity
      );
    }

    return null;
  }

  getInvalidPhysicalProductQuantities(
    physicalProductDetails: FoundryPhysicalProduct[]
  ): FoundryApiError[] {
    if (!this.physicalProducts) {
      return [];
    }

    return CartDetails.getInvalidQuantities(
      this.physicalProducts,
      physicalProductDetails
    );
  }

  static getInvalidQuantities(
    physicalProductsState: PhysicalProductsState,
    physicalProductDetails: FoundryPhysicalProduct[]
  ) {
    return physicalProductsState
      .map((product) => {
        return CartDetails.getPhysicalProductError(
          physicalProductDetails,
          product
        );
      })
      .filter((res) => res != null);
  }
}

const getCartLicenseHolder = (): LicenseHolder | undefined => {
  const licenseHolder = getLicenseHolder();
  if (!licenseHolder) {
    return;
  }

  return {
    name: licenseHolder.license_holder_name,
    emailAddress: licenseHolder.license_holder_email_address,
    companySize:
      (Object.keys(licenseHolder.license_holder_company_size).find(
        (key) => licenseHolder.license_holder_company_size[key]
      ) as LicenseHolderCompanySize) || "",
    addOns: {
      shareWithThirdParty: licenseHolder.addons_share_with_third_party,
    },
  };
};

const getCartFamilies = (): CartFamilies => {
  const families = {};

  Object.entries(FONT_SETTINGS).forEach(([familyName, details]) => {
    const selectedLicenseTypes = getFontLicenseTypes(familyName);
    const selectedStyles = getFontStyles(familyName);

    if (!selectedLicenseTypes) {
      return;
    }

    if (!selectedStyles) {
      families[familyName] = {
        items: [],
        platforms: Object.keys(selectedLicenseTypes).filter(
          (key) => selectedLicenseTypes[key]
        ),
      };
      return;
    }

    let stylesToAdd: number[] = Object.keys(selectedStyles.styles)
      .filter((key) => selectedStyles.styles[key])
      .flatMap((n) => {
        try {
          const num = parseInt(n);
          if (!Number.isNaN(num)) {
            return [num];
          }

          // TODO: sentry
          console.error(`Failed to parse int from ${n}`);
          return [];
        } catch (e) {
          // TODO: sentry
          console.error(`Failed to parse int from ${n}`, e);
          return [];
        }
      });

    const items: Product[] = [];

    for (const bundle of details.stylePurchaseBundles) {
      let bundleActivated = bundle.bundleIncludes.every((style) =>
        stylesToAdd.includes(style)
      );

      // Show the full bundle instead of individual styles if it's a complete set.
      if (bundleActivated) {
        const product = PRODUCTS.find((item) => item.name === bundle.name);

        if (!product) {
          throw new Error("TODO");
        }

        items.push(product);

        stylesToAdd = stylesToAdd.filter(
          (styleToAdd) => !bundle.bundleIncludes.includes(styleToAdd)
        );
      }
    }

    stylesToAdd.forEach((style) => {
      const product = PRODUCTS.find((item) => item.id === style);

      if (product === undefined) {
        throw new Error(`Could not find product with ID ${style}`);
      }

      items.push(product);
    });

    families[familyName] = {
      items,
      platforms: Object.keys(selectedLicenseTypes).filter(
        (key) => selectedLicenseTypes[key]
      ),
    };
  });

  return families;
};

const getCartPhysicalProducts = (): PhysicalProductsState => {
  return getPhysicalProducts();
};

export const getUpdatedPhysicalProducts = (
  physicalProducts: PhysicalProductsState | undefined,
  contentfulId: string,
  change: { quantity?: number; quantityChange?: number }
): PhysicalProductsState => {
  // TODO: validate state
  if (change.quantity === undefined && change.quantityChange === undefined) {
    throw Error("TODO");
  }

  let newState = physicalProducts ? structuredClone(physicalProducts) : [];
  const item = newState.find((item) => item.contentfulId === contentfulId);

  if (item != undefined) {
    if (change.quantity !== undefined) {
      item.quantity = change.quantity;
    } else if (change.quantityChange !== undefined) {
      item.quantity += change.quantityChange;
    }
  } else {
    let quantity = 0;
    if (change.quantity !== undefined) {
      quantity = change.quantity;
    } else if (change.quantityChange !== undefined) {
      quantity += change.quantityChange;
    }
    newState.push({
      contentfulId: contentfulId,
      quantity: quantity,
    });
  }

  return newState.filter((p) => p.quantity > 0);
};
